/**
 * HistoryDrawer — slide-over from the left, behind the icon rail.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { History as HistoryIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useShallow } from "zustand/react/shallow";
import { fetchMultiAgentRunDetail, isMultiAgentAuthError, useRecentRuns } from "@/hooks/multi-agent";
import { humanizeStatus } from "@/services/multi-agent";
import { Button } from "@/components/atoms/multi-agent/Button";
import { StatusDot, type DotStatus } from "@/components/atoms/multi-agent/StatusDot";

const STATUS_TO_DOT: Record<string, DotStatus> = {
  done:               "ok",
  failed:             "err",
  cancelled:          "neutral",
  running:            "busy",
  queued:             "warn",
  awaiting_approval:  "warn",
};

function formatAge(iso?: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HistoryDrawer({ open, onClose }: HistoryDrawerProps) {
  const queryClient = useQueryClient();
  const { activeSessionId, reset, hydrateFromDetail } = useMultiAgentStore(
    useShallow((s) => ({
      activeSessionId: s.sessionId,
      reset: s.reset,
      hydrateFromDetail: s.hydrateFromDetail,
    }))
  );

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { sessions, refetch } = useRecentRuns({ enabled: true });

  // Move focus to the close button when the drawer opens.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => closeButtonRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    // useRecentRuns().refetch returns void (it voids the underlying query
    // promise + surfaces errors via the query's own error state), so we just
    // trigger it — never chain .then/.catch on a non-Promise.
    if (open) refetch();
  }, [open, refetch]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const openSession = async (latestRunId: string) => {
    reset();
    try {
      const d = await fetchMultiAgentRunDetail(queryClient, latestRunId);
      hydrateFromDetail(d);
    } catch (e) {
      if (isMultiAgentAuthError(e)) {
        toast.error("Could not open this session — it may belong to another organization.");
      } else {
        toast.error(`Could not open session: ${(e as Error)?.message ?? String(e)}`);
      }
    }
    onClose();
  };

  return (
    <>
      {/* Translucent overlay — click-outside to dismiss */}
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 20,
          background: "transparent",
          pointerEvents: open ? "auto" : "none",
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Session history"
        className="ma-drawer"
        data-open={open ? "true" : "false"}
        aria-hidden={!open}
        style={{ left: 0 }}
      >
      <header
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--ma-border)" }}
      >
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-4 w-4" style={{ color: "var(--ma-text-muted)" }} />
          <span className="ma-micro" style={{ color: "var(--ma-text)" }}>
            Sessions · {sessions.length >= 40 ? "40+" : sessions.length}
          </span>
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          title="Close history (Esc)"
          aria-label="Close history"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted"
          style={{ color: "var(--ma-text-muted)" }}
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-auto px-3 py-3">
        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-muted-foreground">
            <div className="text-[15px] font-medium text-foreground">No sessions yet</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em]">Ask a question — the session shows up here</div>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {sessions.slice(0, 40).map((session) => {
              const isActive = session.id === activeSessionId;
              const dot = STATUS_TO_DOT[session.status || ""] ?? "neutral";
              return (
                <li key={session.id}>
                  <button
                    type="button"
                    onClick={() => openSession(session.latestRunId)}
                    className="ma-interactive ma-fade-up flex w-full items-start gap-3 px-3 py-2.5 text-left"
                    style={{
                      borderRadius: "var(--ma-radius-md)",
                      background: isActive ? "var(--ma-grad-accent)" : "var(--ma-grad-surface)",
                      boxShadow: isActive
                        ? "var(--ma-shadow-md), inset 0 0 0 1px hsl(var(--primary) / 0.4)"
                        : "var(--ma-shadow-sm)",
                    }}
                  >
                    <span style={{ paddingTop: 5 }}>
                      <StatusDot status={dot} label={null} />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span
                        className="line-clamp-2"
                        style={{
                          color: "var(--ma-text)",
                          fontSize: 13,
                          fontWeight: 500,
                          lineHeight: 1.4,
                          letterSpacing: -0.005,
                        }}
                      >
                        {session.title || "(no intent)"}
                      </span>
                      <span
                        className="ma-micro"
                        style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
                      >
                        {session.run_count} run{session.run_count === 1 ? "" : "s"} · {session.lane || "free"} · {formatAge(session.updated_at)}
                        {session.status && !["done", "cancelled"].includes(session.status) && (
                          <span style={{ color: dot === "err" ? "var(--ma-state-err)" : dot === "busy" ? "var(--ma-accent)" : "var(--ma-state-warn)" }}>
                            {" · "}{humanizeStatus(session.status)}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer
        className="flex items-center gap-2 px-3 py-3"
        style={{ borderTop: "1px solid var(--ma-border)" }}
      >
        <Button
          variant="primary"
          size="sm"
          onClick={() => { useMultiAgentStore.getState().clearThread(); reset(); onClose(); }}
        >
          New session
        </Button>
        <Link to="/weave/runs" className="ml-auto">
          <Button variant="ghost" size="sm">All sessions</Button>
        </Link>
      </footer>
    </aside>
    </>
  );
}
